import { CloseRounded } from "@mui/icons-material";
import { Box, Button, Dialog, DialogContent, IconButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { AdminInvitationValidate, IInvitationDecoded } from "./admins/AdminAcceptInvitation";
import { colors } from "./ui/colors";

interface AdminInvitationDialogProps {
  open: boolean;
  invitationUid: string;
  onClose: () => void;
  onAccepted?: (invitation: IInvitationDecoded) => void | Promise<void>;
  onRejected?: () => void;
}

export const AdminInvitationDialog = ({ open, invitationUid, onClose, onAccepted, onRejected }: AdminInvitationDialogProps) => {
  const [confirmReject, setConfirmReject] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmReject(false);
    }
  }, [open, invitationUid]);

  const handleClose = () => {
    setConfirmReject(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 30000,
        "& .MuiBackdrop-root": {
          zIndex: 29999,
        },
        "& .MuiDialog-container": {
          zIndex: 30000,
        },
      }}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 560,
          borderRadius: "24px",
          overflow: "visible",
          bgcolor: "transparent",
          boxShadow: "none",
          m: 2,
          zIndex: 30001,
          position: "relative",
        },
      }}
      BackdropProps={{
        sx: {
          bgcolor: "rgba(15, 23, 42, 0.42)",
          backdropFilter: "blur(6px)",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          overflow: "visible",
          position: "relative",
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 14,
            right: 14,
            zIndex: 30002,
            width: 36,
            height: 36,
            borderRadius: "12px",
            bgcolor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.92)",
            border: "1px solid rgba(229,231,235,0.95)",
            boxShadow: "0px 8px 22px rgba(15, 23, 42, 0.14)",
            "&:hover": {
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
            },
          }}
        >
          <CloseRounded sx={{ fontSize: 19 }} />
        </IconButton>

        {confirmReject ? (
          <Box
            sx={{
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              borderRadius: "24px",
              p: 4,
              textAlign: "center",
              boxShadow: "0px 24px 70px rgba(15, 23, 42, 0.22)",
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 1000, color: colors.gray.base, mb: 1 }}>Decline Invitation</Typography>

            <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.gray[500], mb: 3 }}>
              Are you sure you want to decline this company invitation?
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "center", gap: 1.5 }}>
              <Button
                onClick={handleClose}
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 900,
                  color: colors.gray[500],
                  bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
                  px: 2.5,
                  "&:hover": {
                    bgcolor: "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.07)",
                  },
                }}
              >
                Maybe later
              </Button>

              <Button
                variant="contained"
                onClick={async () => {
                  await onRejected?.();
                }}
                sx={{
                  borderRadius: "999px",
                  textTransform: "none",
                  fontWeight: 900,
                  bgcolor: "#B91C1C",
                  px: 2.5,
                  boxShadow: "none",
                  "&:hover": {
                    bgcolor: "#991B1B",
                    boxShadow: "none",
                  },
                }}
              >
                Decline
              </Button>
            </Box>
          </Box>
        ) : (
          <AdminInvitationValidate
            variant="modal"
            invitationUid={invitationUid}
            onClose={handleClose}
            onAccepted={onAccepted}
            onRejected={onRejected}
            onRejectClick={() => setConfirmReject(true)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
